import { useCallback, useEffect, useRef, useState } from 'react';
import {
    ScheduleHoursJornadaDTO,
    ServiceStatisticsFilter,
    ServiceStatisticsGranularity,
    ServiceStatisticsHourlyDTO,
    ServiceStatisticsLongestWaitDTO,
    ServiceStatisticsServiceDTO,
    ServiceStatisticsSummaryDTO,
    ServiceStatisticsTimelineRowDTO,
    WaitRankingRowDTO,
} from '../../dto/ServiceStatisticsDTO';
import { parseApiError } from '../../services/apiError';
import {
    getServiceStatisticsHourly,
    getServiceStatisticsLongestWaits,
    getServiceStatisticsScheduleHours,
    getServiceStatisticsServices,
    getServiceStatisticsSummary,
    getServiceStatisticsTimeline,
    getServiceStatisticsWaitRanking,
    StatisticsAccessProblem,
    statisticsAccessProblem,
} from '../../services/ServiceStatisticsController';

/** Cuántas filas se piden del ranking del período: alcanza para todos los servicios con 30 atenciones o más. */
const RANKING_FETCH_LIMIT = 100;

/** Cuántas atenciones con más espera se ven primero y cuántas con «Ver más» (el máximo del backend). */
export const LONGEST_WAITS_FIRST = 5;
export const LONGEST_WAITS_MORE = 20;

/** Lo que se pide de un período: sus filtros (con las fechas del período) y, con un día y una agenda elegida, su horario. */
export interface PeriodDetailRequest {
    filter: ServiceStatisticsFilter;
    granularity: ServiceStatisticsGranularity;
    /** Agenda de consultorio de la que se pide la jornada del día; 0 si no hay. */
    scheduleAgendaId: number;
    /** Se mira un solo servicio: no hace falta la lista de servicios. */
    skipServices?: boolean;
}

/** Las tres consultas que van juntas: indicadores, servicios y, según el período, la curva por hora o el desglose por día. */
export interface PeriodDetailCore {
    summary: ServiceStatisticsSummaryDTO;
    services: ServiceStatisticsServiceDTO[];
    /** Solo de un día. */
    hourly: ServiceStatisticsHourlyDTO | null;
    /** Solo de una semana o un mes: un día por fila (los días sin atenciones no vienen). */
    days: ServiceStatisticsTimelineRowDTO[] | null;
    /** Solo de un día y con una agenda de consultorio elegida, si tuvo jornada ese día. */
    jornada: ScheduleHoursJornadaDTO | null;
}

export type SectionStatus = 'loading' | 'ready' | 'failed';

export interface PeriodDetailState {
    core: { status: SectionStatus; data: PeriodDetailCore | null };
    ranking: { status: SectionStatus; rows: WaitRankingRowDTO[] };
    /** Las atenciones que más esperaron: llamada aparte, con su propia carga y su propio error. */
    longest: { status: SectionStatus; rows: ServiceStatisticsLongestWaitDTO[]; expanded: boolean };
    /** Vuelve a pedir lo del período, sin mirar el caché (el aviso «Reintentar»). */
    retryCore: () => void;
    retryRanking: () => void;
    retryLongest: () => void;
    /** «Ver más»: pide hasta `LONGEST_WAITS_MORE` atenciones. */
    showMoreLongest: () => void;
}

interface Result<T> {
    key: string;
    /** Null si la consulta falló. */
    data: T | null;
}

/**
 * Los datos del panel de detalle de un período. Indicadores, servicios y horas (o días) se piden juntos: si una falla, el
 * panel avisa una sola vez con «Reintentar». El ranking de causas va en una llamada aparte: si falla, solo su sección
 * avisa. Cada resultado queda en un caché por período y filtros (los filtros forman parte de la clave, así que no hace
 * falta invalidarlo) y una consulta que ya no se ve se cancela. Sin permiso, avisa con `onAccessProblem`. Con `request`
 * null (panel cerrado) no consulta y devuelve lo último que mostró.
 */
export function usePeriodDetail(
    request: PeriodDetailRequest | null,
    onAccessProblem: (problem: StatisticsAccessProblem) => void,
): PeriodDetailState {
    const requestKey = request ? JSON.stringify(request) : '';
    // El ranking no se pide por servicio (el panel acota la fila): cambiar de servicio no lo vuelve a consultar
    const rankingKey = request ? JSON.stringify({ ...request.filter, serviceId: undefined }) : '';
    // Las atenciones con más espera sí van por servicio; el límite es parte de la clave
    const longestBaseKey = request ? JSON.stringify(request.filter) : '';
    const [expandedKey, setExpandedKey] = useState('');
    const expanded = longestBaseKey !== '' && expandedKey === longestBaseKey;
    const longestLimit = expanded ? LONGEST_WAITS_MORE : LONGEST_WAITS_FIRST;
    const longestKey = longestBaseKey === '' ? '' : `${longestBaseKey}|${longestLimit}`;
    const coreCache = useRef(new Map<string, PeriodDetailCore>());
    const rankingCache = useRef(new Map<string, WaitRankingRowDTO[]>());
    const longestCache = useRef(new Map<string, ServiceStatisticsLongestWaitDTO[]>());
    const lastState = useRef<PeriodDetailState | null>(null);
    const accessRef = useRef(onAccessProblem);
    accessRef.current = onAccessProblem;
    const [coreResult, setCoreResult] = useState<Result<PeriodDetailCore> | null>(null);
    const [rankingResult, setRankingResult] = useState<Result<WaitRankingRowDTO[]> | null>(null);
    const [longestResult, setLongestResult] = useState<Result<ServiceStatisticsLongestWaitDTO[]> | null>(null);
    const [coreReload, setCoreReload] = useState(0);
    const [longestReload, setLongestReload] = useState(0);
    const [rankingReload, setRankingReload] = useState(0);

    useEffect(() => {
        if (requestKey === '') return undefined;
        const cached = coreCache.current.get(requestKey);
        if (cached) {
            setCoreResult({ key: requestKey, data: cached });
            return undefined;
        }
        let cancelled = false;
        const { filter, granularity, scheduleAgendaId, skipServices } = JSON.parse(requestKey) as PeriodDetailRequest;
        const isDay = granularity === 'DAY';
        Promise.all([
            getServiceStatisticsSummary(filter),
            skipServices ? Promise.resolve([] as ServiceStatisticsServiceDTO[]) : getServiceStatisticsServices(filter),
            isDay ? getServiceStatisticsHourly(filter) : Promise.resolve(null),
            isDay ? Promise.resolve(null) : getServiceStatisticsTimeline(filter, 'DAY'),
            // La jornada es un dato de más: si falla, el resto del día se ve igual
            isDay && scheduleAgendaId > 0
                ? getServiceStatisticsScheduleHours(filter, scheduleAgendaId)
                    .then(result => result.jornadas.find(item => item.date === filter.initialDate) ?? null)
                    .catch((error: unknown) => {
                        const problem = statisticsAccessProblem(parseApiError(error));
                        if (problem) throw error;
                        return null;
                    })
                : Promise.resolve(null),
        ])
            .then(([summary, services, hourly, days, jornada]) => {
                const core: PeriodDetailCore = { summary, services, hourly, days, jornada };
                coreCache.current.set(requestKey, core);
                if (!cancelled) setCoreResult({ key: requestKey, data: core });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                const problem = statisticsAccessProblem(parseApiError(error));
                if (problem) accessRef.current(problem);
                else setCoreResult({ key: requestKey, data: null });
            });
        return () => { cancelled = true; };
    }, [requestKey, coreReload]);

    useEffect(() => {
        if (rankingKey === '') return undefined;
        const cached = rankingCache.current.get(rankingKey);
        if (cached) {
            setRankingResult({ key: rankingKey, data: cached });
            return undefined;
        }
        let cancelled = false;
        getServiceStatisticsWaitRanking(JSON.parse(rankingKey) as ServiceStatisticsFilter, RANKING_FETCH_LIMIT)
            .then(result => {
                rankingCache.current.set(rankingKey, result.rows);
                if (!cancelled) setRankingResult({ key: rankingKey, data: result.rows });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                const problem = statisticsAccessProblem(parseApiError(error));
                if (problem) accessRef.current(problem);
                else setRankingResult({ key: rankingKey, data: null });
            });
        return () => { cancelled = true; };
    }, [rankingKey, rankingReload]);

    useEffect(() => {
        if (longestKey === '') return undefined;
        const cached = longestCache.current.get(longestKey);
        if (cached) {
            setLongestResult({ key: longestKey, data: cached });
            return undefined;
        }
        let cancelled = false;
        getServiceStatisticsLongestWaits(JSON.parse(longestBaseKey) as ServiceStatisticsFilter, longestLimit)
            .then(rows => {
                longestCache.current.set(longestKey, rows);
                if (!cancelled) setLongestResult({ key: longestKey, data: rows });
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                const problem = statisticsAccessProblem(parseApiError(error));
                if (problem) accessRef.current(problem);
                else setLongestResult({ key: longestKey, data: null });
            });
        return () => { cancelled = true; };
    }, [longestKey, longestBaseKey, longestLimit, longestReload]);

    const retryCore = useCallback(() => {
        coreCache.current.delete(requestKey);
        setCoreResult(null);
        setCoreReload(count => count + 1);
    }, [requestKey]);
    const retryRanking = useCallback(() => {
        rankingCache.current.delete(rankingKey);
        setRankingResult(null);
        setRankingReload(count => count + 1);
    }, [rankingKey]);

    const retryLongest = useCallback(() => {
        longestCache.current.delete(longestKey);
        setLongestResult(null);
        setLongestReload(count => count + 1);
    }, [longestKey]);
    const showMoreLongest = useCallback(() => setExpandedKey(longestBaseKey), [longestBaseKey]);

    const coreCurrent = coreResult?.key === requestKey ? coreResult : null;
    const rankingCurrent = rankingResult?.key === rankingKey ? rankingResult : null;
    // Al pedir más, las cinco primeras se siguen viendo mientras llegan las demás
    const longestCurrent = longestResult?.key === longestKey ? longestResult : null;
    const longestFirst = expanded && longestCurrent === null && longestResult?.key === `${longestBaseKey}|${LONGEST_WAITS_FIRST}`
        ? longestResult : null;
    // Con el panel cerrado no se consulta nada, pero mientras se desliza hacia afuera sigue mostrando lo último
    if (requestKey === '' && lastState.current) return lastState.current;
    const state: PeriodDetailState = {
        core: {
            status: coreCurrent === null ? 'loading' : coreCurrent.data ? 'ready' : 'failed',
            data: coreCurrent?.data ?? null,
        },
        ranking: {
            status: rankingCurrent === null ? 'loading' : rankingCurrent.data ? 'ready' : 'failed',
            rows: rankingCurrent?.data ?? [],
        },
        longest: {
            status: longestCurrent === null ? 'loading' : longestCurrent.data ? 'ready' : 'failed',
            rows: longestCurrent?.data ?? longestFirst?.data ?? [],
            expanded,
        },
        retryCore,
        retryRanking,
        retryLongest,
        showMoreLongest,
    };
    if (requestKey !== '') lastState.current = state;
    return state;
}
